import {FC, MouseEvent, useState} from 'react';
import {ReceiptLongOutlined} from '@mui/icons-material';
import {Box, Popover} from '@mui/material';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneFact} from 'src/components/mobile/PhoneCardFacts/PhoneCardFacts.types';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {invoiceTableCardSx} from './InvoiceTableCard.sx';
import {ColoredStatus} from '../../../common/ColoredStatus';
import {DownloadInvoicePdf} from '../DownloadInvoicePdf';
import {InvoiceNumberLink} from '../InvoiceNumberLink';
import {InvoicePreview} from '../../InvoicePreview';
import {InvoiceTableCardTypes} from './InvoiceTableCard.types';
import {RevenueInvoicePreview} from '../../RevenueInvoicePreview';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';

export const InvoiceTableCard: FC<InvoiceTableCardTypes> = ({adminData, businessData, revenue}) => {
    const intl = useIntl();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const data = adminData ?? businessData;
    const showRevenue = revenue && adminData;
    const showInvoice = !revenue && businessData;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // One node, drawn by whichever of the two the width calls for: the handler behind it is the
    // same in both.
    const downloadAction = data ? (
        <DownloadInvoicePdf
            revenue={revenue}
            invoiceId={data.id}
            name={data.invoiceNumber}
            businessId={revenue ? adminData?.business?.id : undefined}
            userId={data.user?.id}
            isIcon={false}
        />
    ) : null;

    // The menu and the overlay it opens travel together into the head's action slot: the popover
    // anchors on the button's element, not on where it sits in the card.
    const menuAction = data?.bexioId ? (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<ReceiptLongOutlined/>}
                    title={data?.invoiceNumber}
                    subtitle={data?.created ? new Date(data.created).toLocaleString('fr-CH') : undefined}
                >
                    {downloadAction}
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={invoiceTableCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {downloadAction}
                </Popover>
            )}
        </>
    ) : null;

    const facts: PhoneFact[] = [
        {
            label: intl.formatMessage({id: 'invoiceDateColumnHeader'}),
            value: data?.created ? new Date(data.created).toLocaleString('fr-CH') : '-',
        },
        {
            label: intl.formatMessage({id: 'invoiceAmountColumnHeader'}),
            value: data?.bexioAmount || data?.amount ? `${data?.bexioAmount || data?.amount / 100} ${data?.currency || ''}` : '0',
        },
    ];

    // Only a business invoice is metered — a revenue invoice is issued to a business and has no
    // consumption behind it, so the fact is left off rather than shown as a zero.
    if (businessData?.consumedWattsPoint) {
        facts.push({
            label: intl.formatMessage({id: 'consumption'}),
            value: `${(businessData.consumedWattsPoint / 1000).toFixed(3)} ${intl.formatMessage({id: 'kWh'})}`,
        });
    }

    return (
        <TableCard>
            <PhoneCardHead
                icon={<ReceiptLongOutlined/>}
                title={
                    revenue ? (
                        <>{data?.invoiceNumber}</>
                    ) : (
                        <InvoiceNumberLink
                            invoiceId={data?.id}
                            invoiceNumber={data?.invoiceNumber}
                        />
                    )
                }
                subtitle={
                    <>
                        {showRevenue && <RevenueInvoicePreview data={adminData}/>}
                        {showInvoice && <InvoicePreview data={businessData}/>}
                    </>
                }
                action={menuAction}
            />
            {data?.status && (
                <Box sx={phonePillsRowSx}>
                    <ColoredStatus label={data.status}/>
                </Box>
            )}
            <PhoneCardFacts facts={facts}/>
        </TableCard>
    );
};
